import React, { useState, useEffect } from "react";
import { FieldValues, Control } from "react-hook-form";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import SelectComponent from "../form/SelectComponent";
import addProductModalImg from "../../../addproduct-modal.png";

interface AddProductModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly items: { index: number; productName: string }[];
  readonly customerID: string;
  readonly control: Control<any>;
  readonly onAddProduct: (data: FieldValues) => void;
}

export default function AddProductModal(props: AddProductModalProps) {
  const { isOpen, onClose, items, customerID, control, onAddProduct } = props;
  const { t } = useTranslation();
  const [productSelected, setProductSelected] = useState<string | undefined>();
  const [hasVariants, setHasVariants] = useState<boolean>(false);
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [formData, setFormData] = useState<any>({});

  const lastIndex = items.length ? items[items.length - 1].index + 1 : 1;

  useEffect(() => {   
  if (isOpen && !selectedPosition) {
      const defaultPos = String(lastIndex);
      setSelectedPosition(defaultPos);
      setFormData(prev => {
        const newData = { 
          ...prev, 
          position: defaultPos,
          quantity: "1"
        };
        return newData;
      });
    }
  }, [isOpen, lastIndex, selectedPosition]);

  const onSubmit = () => {
    const finalFormData = { ...formData };
    if (!productSelected || !hasVariants) {
      finalFormData.variant = { value: null, label: null };
    }
    
    onAddProduct(finalFormData);
    
    setProductSelected(undefined);
    setHasVariants(false);
    setSelectedPosition("");
    setFormData({});
    onClose();
  };

  const handleProductSelectionChange = (newValue: any) => {

    if (newValue?.value) {
      setProductSelected(newValue.value);
      
      setFormData(prev => {
        const newData = { ...prev, product: newValue };
        return newData;
      });
    } else {
      setProductSelected(undefined);
      setFormData(prev => {
        const { product, variant, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleVariantChange = (newValue: any) => {
    setFormData(prev => {
      const newData = { ...prev, variant: newValue };
      return newData;
    });
  };

  const handleQuantityChange = (newValue: string) => {
    setFormData(prev => {
      const newData = { ...prev, quantity: newValue };
      return newData;
    });
  };

  const handlePositionChange = (keys: any) => {
    const selectedValue = Array.from(keys)[0] as string;
    setSelectedPosition(selectedValue);
    setFormData(prev => {
      const newData = { ...prev, position: selectedValue };
      return newData;
    });
  };

  const selectItems = [
    <SelectItem key={String(lastIndex)} textValue={t("last_position")}>
      {t("last_position")}
    </SelectItem>,
    ...items.map((item) => (
      <SelectItem
        key={String(item.index)}
        textValue={`${t("before")} ${item.index + 1} / ${item.productName}`}
      >
        {t("before")} {item.index + 1} / {item.productName}
      </SelectItem>
    )),
  ];

  const isFormValid = selectedPosition && formData.product && formData.quantity;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      backdrop="opaque"
      placement="top-center"
      size="2xl"
      classNames={{
        backdrop: "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
      }}
    >
      <ModalContent>
        <ModalBody>
          <div className="flex pt-5">
            <div>
              <div className="text-2xl font-bold pt-3 pb-6">{t("add_product")}</div>
              <div className="text-vistagreen">{t("product_missing_prompt")}</div>
            </div>
            <img
              src={addProductModalImg}
              alt={t("vistaflow_archive")}
              className="w-48 origin-left self-center"
            />
          </div>

          <div className="font-bold">{t("position")}:</div>
          <Select
            aria-label={t("select_product_position")}
            data-cy="addProduct__select-position"
            placeholder={t("position")}
            labelPlacement="outside"
            className="max-w-lg"
            disableSelectorIconRotation
            disallowEmptySelection
            selectedKeys={selectedPosition ? [selectedPosition] : []}
            onSelectionChange={handlePositionChange}
          >
            {selectItems}
          </Select>

          <div className="font-bold">{t("product")}:</div>
          <div data-cy="addProduct__select-product">
            <SelectComponent
              apiUrl="products"
              endpoint="products"
              customerId={customerID}
              control={control}
              defaultValue={null}
              name="modal_product"
              onSelectionChange={(selectedOption) => {
                if (selectedOption) {
                  handleProductSelectionChange(selectedOption);
                } else {
                  handleProductSelectionChange(null);
                }
              }}
            />
          </div>

          {productSelected && (
            <>
              {hasVariants && <div className="font-bold">{t("variant")}:</div>}
              <SelectComponent
                apiUrl="products"
                endpoint="variants"
                customerId={customerID}
                productId={productSelected}
                defaultValue={null}
                control={control}
                name="modal_variant"
                variantsAvailable={(available) => {
                  setHasVariants(available);
                }}
                onSelectionChange={handleVariantChange}
              />
            </>
          )}

          <div className="font-bold">{t("quantity")}:</div>
          <Input
            type="number"
            min="1"
            value={formData.quantity || "1"}
            classNames={{
              inputWrapper: `h-8`,
            }}
            onChange={(e) => {
              handleQuantityChange(e.target.value);
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button 
            color="default" 
            variant="flat" 
            onPress={() => {
              onClose();
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            data-cy="addProduct__submit-button"
            className={`bg-vistagreen text-white ${
              !isFormValid ? "bg-gray-400 cursor-not-allowed" : ""
            }`}
            disabled={!isFormValid}
            onPress={() => {
              onSubmit();
            }}
          >
            {t("add_product")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}